import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import LanguageSwitcher from "../../../shared/components/LanguageSwitcher/index.jsx";
import styles from "../../../styles/login.module.css";
import defaultLogo from "../../../images/logo2.png";
import appConfig from "../../../config/appConfig";
import { useTenantBranding } from "../../../shared/branding/TenantBrandingProvider";
import {
  setToken,
  setUserPermissions,
  logout,
} from "../../../shared/lib/auth/authHelper";
import { resolveAcademyEntryRoute } from "../../academy/lib/resolveAcademyEntryRoute";

export default function Login() {
  const { t } = useTranslation();
  const { branding } = useTenantBranding();
  const navigate = useNavigate();
  const location = useLocation();

  const [step, setStep] = useState("login");

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  const [resetEmail, setResetEmail] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  const [googleConfigLoading, setGoogleConfigLoading] = useState(true);
  const [googleEnabled, setGoogleEnabled] = useState(false);
  const [googleClientId, setGoogleClientId] = useState("");
  const [googleRedirectUri, setGoogleRedirectUri] = useState("");
  const [googleFrontendSuccessUrl, setGoogleFrontendSuccessUrl] = useState("");
  const [googleFrontendErrorUrl, setGoogleFrontendErrorUrl] = useState("");
  const [googleLoading, setGoogleLoading] = useState(false);

  const fallbackRedirectUri = useMemo(() => {
    return `${window.location.origin}/`;
  }, []);

  const fallbackFrontendSuccessUrl = useMemo(() => {
    return `${window.location.origin}/menu/dashboard`;
  }, []);

  const fallbackFrontendErrorUrl = useMemo(() => {
    return `${window.location.origin}/`;
  }, []);

  const allowPublicRegister =
    branding?.allow_public_employee_registration !== false;

  const resetForgotPasswordState = () => {
    setResetEmail("");
    setResetCode("");
    setNewPassword("");
  };

  const goToLoginStep = () => {
    setStep("login");
    setError("");
    setSuccess("");
    resetForgotPasswordState();
  };

  const goToForgotPasswordStep = () => {
    setStep("forgot-email");
    setError("");
    setSuccess("");
    resetForgotPasswordState();
  };

  const validateLoginForm = () => {
    if (!username || !password) {
      setError(
        t("error_required_fields", {
          defaultValue: "Please fill in all required fields",
        })
      );
      return false;
    }
    setError("");
    return true;
  };

  const validateForgotEmailStep = () => {
    if (!resetEmail.trim()) {
      setError(
        t("error_required_fields", {
          defaultValue: "Please fill in all required fields",
        })
      );
      return false;
    }
    setError("");
    return true;
  };

  const validateForgotCodeStep = () => {
    if (!resetEmail.trim() || !resetCode.trim()) {
      setError(
        t("error_required_fields", {
          defaultValue: "Please fill in all required fields",
        })
      );
      return false;
    }
    setError("");
    return true;
  };

  const validateForgotCompleteStep = () => {
    if (!resetEmail.trim() || !resetCode.trim() || !newPassword.trim()) {
      setError(
        t("error_required_fields", {
          defaultValue: "Please fill in all required fields",
        })
      );
      return false;
    }

    if ((newPassword || "").length < 8) {
      setError(
        t("password_min_length", {
          defaultValue: "Password must be at least 8 characters",
        })
      );
      return false;
    }

    setError("");
    return true;
  };

  const loadPermissionsAndEnter = async (accessToken) => {
    const permissionsResponse = await fetch(
      `${appConfig.API_BASE_URL}/auth/me/permissions`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        credentials: "include",
      }
    );

    if (!permissionsResponse.ok) {
      const errorData = await permissionsResponse.json().catch(() => ({}));
      logout();
      throw new Error(errorData.detail || "Failed to load permissions");
    }

    const permissions = await permissionsResponse.json();
    setUserPermissions(Array.isArray(permissions) ? permissions : []);

    localStorage.removeItem("session-exp-ms");
    window.dispatchEvent(new Event("token-changed"));
    window.dispatchEvent(new Event("permissions-updated"));

    const entryRoute = await resolveAcademyEntryRoute(accessToken);
    navigate(entryRoute, { replace: true });
    return entryRoute;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateLoginForm()) return;

    setLoading(true);
    setError("");
    setSuccess("");

    const formDetails = new URLSearchParams();
    formDetails.append("username", username);
    formDetails.append("password", password);

    try {
      logout();

      const response = await fetch(`${appConfig.API_BASE_URL}/auth/token`, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: formDetails,
        credentials: "include",
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.detail || t("error_auth_failed"));
        return;
      }

      const data = await response.json();
      const accessToken = data?.access_token;

      if (!accessToken) {
        setError(t("error_auth_failed"));
        return;
      }

      setToken(accessToken);
      await loadPermissionsAndEnter(accessToken);
    } catch {
      logout();
      setError(t("error_generic"));
    } finally {
      setLoading(false);
    }
  };

  const handleRequestPasswordResetCode = async (event) => {
    event.preventDefault();
    if (!validateForgotEmailStep()) return;

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const response = await fetch(
        `${appConfig.API_BASE_URL}/auth/password-reset/request-code`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            email: resetEmail.trim(),
          }),
        }
      );

      const responseData = await response.json().catch(() => ({}));

      if (!response.ok) {
        setError(
          responseData.detail ||
            responseData.message ||
            t("error_generic", { defaultValue: "Something went wrong" })
        );
        return;
      }

      setStep("forgot-code");
      setSuccess(
        t("password_reset_code_sent", {
          defaultValue: "We sent a password reset code to your email",
        })
      );
    } catch {
      setError(t("error_generic", { defaultValue: "Something went wrong" }));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyPasswordResetCode = async (event) => {
    event.preventDefault();
    if (!validateForgotCodeStep()) return;

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const response = await fetch(
        `${appConfig.API_BASE_URL}/auth/password-reset/verify-code`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            email: resetEmail.trim(),
            code: resetCode.trim(),
          }),
        }
      );

      const responseData = await response.json().catch(() => ({}));

      if (!response.ok) {
        setError(
          responseData.detail ||
            responseData.message ||
            t("error_generic", { defaultValue: "Something went wrong" })
        );
        return;
      }

      setStep("forgot-complete");
      setSuccess(
        t("password_reset_code_verified", {
          defaultValue: "Code verified successfully",
        })
      );
    } catch {
      setError(t("error_generic", { defaultValue: "Something went wrong" }));
    } finally {
      setLoading(false);
    }
  };

  const handleCompletePasswordReset = async (event) => {
    event.preventDefault();
    if (!validateForgotCompleteStep()) return;

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const response = await fetch(
        `${appConfig.API_BASE_URL}/auth/password-reset/complete`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            email: resetEmail.trim(),
            code: resetCode.trim(),
            password: newPassword,
          }),
        }
      );

      const responseData = await response.json().catch(() => ({}));

      if (!response.ok) {
        setError(
          responseData.detail ||
            responseData.message ||
            t("error_generic", { defaultValue: "Something went wrong" })
        );
        return;
      }

      setSuccess(
        t("password_reset_success", {
          defaultValue: "Password changed successfully. You can now sign in.",
        })
      );
      setStep("login");
      setPassword("");
      resetForgotPasswordState();
    } catch {
      setError(t("error_generic", { defaultValue: "Something went wrong" }));
    } finally {
      setLoading(false);
    }
  };

  const handleResendPasswordResetCode = async () => {
    if (!validateForgotEmailStep()) return;

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const response = await fetch(
        `${appConfig.API_BASE_URL}/auth/password-reset/request-code`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            email: resetEmail.trim(),
          }),
        }
      );

      const responseData = await response.json().catch(() => ({}));

      if (!response.ok) {
        setError(
          responseData.detail ||
            responseData.message ||
            t("error_generic", { defaultValue: "Something went wrong" })
        );
        return;
      }

      setSuccess(
        t("password_reset_code_resent", {
          defaultValue: "Password reset code sent again",
        })
      );
    } catch {
      setError(t("error_generic", { defaultValue: "Something went wrong" }));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    setError("");
    setSuccess("");

    if (!googleEnabled) return;

    if (!googleClientId) {
      setError(
        t("google_client_id_missing", {
          defaultValue: "Google OAuth is not configured for this tenant yet",
        })
      );
      return;
    }

    const redirectUri = googleRedirectUri || fallbackRedirectUri;

    const params = new URLSearchParams({
      client_id: googleClientId,
      redirect_uri: redirectUri,
      response_type: "code",
      scope: "openid email profile",
      access_type: "offline",
      prompt: "select_account",
    });

    window.location.assign(
      `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`
    );
  };

  useEffect(() => {
    let cancelled = false;

    const loadGoogleConfig = async () => {
      try {
        setGoogleConfigLoading(true);

        const response = await fetch(
          `${appConfig.API_BASE_URL}/public/crmGoogleOAuth`,
          {
            method: "GET",
            credentials: "include",
          }
        );

        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          if (cancelled) return;
          setGoogleEnabled(false);
          setGoogleClientId("");
          setGoogleRedirectUri("");
          setGoogleFrontendSuccessUrl("");
          setGoogleFrontendErrorUrl("");
          return;
        }

        if (cancelled) return;

        setGoogleEnabled(!!data?.enabled);
        setGoogleClientId(data?.client_id || "");
        setGoogleRedirectUri(data?.redirect_uri || fallbackRedirectUri);
        setGoogleFrontendSuccessUrl(
          data?.frontend_success_url || fallbackFrontendSuccessUrl
        );
        setGoogleFrontendErrorUrl(
          data?.frontend_error_url || fallbackFrontendErrorUrl
        );
      } catch {
        if (cancelled) return;
        setGoogleEnabled(false);
        setGoogleClientId("");
        setGoogleRedirectUri("");
        setGoogleFrontendSuccessUrl("");
        setGoogleFrontendErrorUrl("");
      } finally {
        if (!cancelled) setGoogleConfigLoading(false);
      }
    };

    loadGoogleConfig();

    return () => {
      cancelled = true;
    };
  }, [
    fallbackRedirectUri,
    fallbackFrontendSuccessUrl,
    fallbackFrontendErrorUrl,
  ]);

  useEffect(() => {
    const search = new URLSearchParams(location.search);
    const code = search.get("code");
    const oauthError = search.get("error");

    if (oauthError) {
      setError(
        t("google_oauth_cancelled", {
          defaultValue: "Google authorization was cancelled or failed",
        })
      );
      return;
    }

    if (!code) return;
    if (!googleEnabled) return;

    let cancelled = false;

    const finishGoogleAuth = async () => {
      try {
        setGoogleLoading(true);
        setError("");
        setSuccess("");
        logout();

        const response = await fetch(
          `${appConfig.API_BASE_URL}/auth/employee/google`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            credentials: "include",
            body: JSON.stringify({ code }),
          }
        );

        const responseData = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(
            responseData.detail ||
              responseData.message ||
              t("error_generic", { defaultValue: "Something went wrong" })
          );
        }

        const accessToken = responseData?.access_token;
        if (!accessToken) {
          throw new Error(
            t("google_access_token_missing", {
              defaultValue: "Google login did not return access token",
            })
          );
        }

        if (cancelled) return;

        setToken(accessToken);
        const entryRoute = await loadPermissionsAndEnter(accessToken);

        const successTarget =
          entryRoute === "/menu/academy"
            ? `${window.location.origin}/menu/academy`
            : googleFrontendSuccessUrl || fallbackFrontendSuccessUrl;
        if (successTarget) {
          window.history.replaceState({}, document.title, successTarget);
        }
      } catch (err) {
        if (cancelled) return;
        logout();
        setError(
          err?.message ||
            t("error_generic", { defaultValue: "Something went wrong" })
        );

        const errorTarget = googleFrontendErrorUrl || fallbackFrontendErrorUrl;
        if (errorTarget) {
          window.history.replaceState({}, document.title, errorTarget);
        }
      } finally {
        if (!cancelled) {
          setGoogleLoading(false);
          const cleanUrl =
            `${window.location.origin}${window.location.pathname}`;
          window.history.replaceState({}, document.title, cleanUrl);
        }
      }
    };

    finishGoogleAuth();

    return () => {
      cancelled = true;
    };
  }, [
    location.search,
    googleEnabled,
    googleFrontendSuccessUrl,
    googleFrontendErrorUrl,
    fallbackFrontendSuccessUrl,
    fallbackFrontendErrorUrl,
    navigate,
    t,
  ]);

  const logoSrc = branding?.logo_url || defaultLogo;
  const showGoogleBlock =
    !googleConfigLoading &&
    googleEnabled &&
    !!googleClientId &&
    !!googleRedirectUri;

  const formDisabled = loading || googleLoading;

  return (
    <div
      className={styles.loginContainer}
      style={{ background: "var(--tenant-login-bg)" }}
    >
      <div className={styles.loginCard}>
        <div className={styles.logoWrap}>
          <img
            src={logoSrc}
            alt={branding?.company_name || "VelSoft CRM"}
            className={styles.logo}
          />
        </div>

        <div className={styles.topBar}>
          <LanguageSwitcher />
        </div>

        <h2>
          {step === "login"
            ? t("login", { defaultValue: "Login" })
            : step === "forgot-email"
            ? t("forgot_password", { defaultValue: "Forgot password?" })
            : step === "forgot-code"
            ? t("verify_reset_code", { defaultValue: "Verify reset code" })
            : t("set_new_password", { defaultValue: "Set new password" })}
        </h2>

        {step === "login" && (
          <form onSubmit={handleSubmit}>
            <div className={styles.inputGroup}>
              <label>{t("username")}:</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder={t("placeholder_username")}
                required
                disabled={formDisabled}
              />
            </div>

            <div className={styles.inputGroup}>
              <label>{t("password")}:</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t("placeholder_password")}
                required
                disabled={formDisabled}
              />
            </div>

            <div className={styles.formMetaRow}>
              {allowPublicRegister ? (
                <Link to="/employee-register" className={styles.registerLink}>
                  {t("employee_register_title", {
                    defaultValue: "Create Employee",
                  })}
                </Link>
              ) : (
                <span />
              )}

              <button
                type="button"
                className={styles.textAction}
                onClick={goToForgotPasswordStep}
                disabled={formDisabled}
              >
                {t("forgot_password", { defaultValue: "Forgot password?" })}
              </button>
            </div>

            <button
              type="submit"
              className={styles.loginButton}
              disabled={formDisabled}
            >
              {loading
                ? t("logging_in", { defaultValue: "Logging in..." })
                : t("login", { defaultValue: "Login" })}
            </button>

            {showGoogleBlock && (
              <div className={styles.socialSection}>
                <span className={styles.socialText}>
                  {t("sign_in_with", {
                    defaultValue: "Sign in with",
                  })}
                </span>

                <button
                  type="button"
                  className={styles.googleBtn}
                  onClick={handleGoogleLogin}
                  disabled={formDisabled}
                >
                  <span className={styles.googleIcon}>
                    <svg viewBox="0 0 48 48">
                      <path
                        fill="#EA4335"
                        d="M24 9.5c3.54 0 6.74 1.22 9.25 3.6l6.9-6.9C35.9 2.1 30.3 0 24 0 14.6 0 6.4 5.4 2.6 13.3l8 6.2C12.5 13.2 17.8 9.5 24 9.5z"
                      />
                      <path
                        fill="#4285F4"
                        d="M46.5 24.5c0-1.6-.1-2.7-.4-3.9H24v7.4h12.7c-.3 1.8-1.9 4.6-5.4 6.5l8.3 6.5c4.8-4.4 7.6-10.8 7.6-18.5z"
                      />
                      <path
                        fill="#FBBC05"
                        d="M10.6 28.5c-.6-1.8-1-3.7-1-5.7s.4-3.9 1-5.7l-8-6.2C1 15.2 0 19.5 0 24s1 8.8 2.6 12.5l8-6z"
                      />
                      <path
                        fill="#34A853"
                        d="M24 48c6.3 0 11.6-2.1 15.4-5.7l-8.3-6.5c-2.3 1.6-5.4 2.7-7.1 2.7-6.2 0-11.5-3.7-13.4-9l-8 6C6.4 42.6 14.6 48 24 48z"
                      />
                    </svg>
                  </span>

                  <span>
                    {googleLoading
                      ? t("google_loading", { defaultValue: "Google..." })
                      : t("google_sign_in", {
                          defaultValue: "Continue with Google",
                        })}
                  </span>
                </button>
              </div>
            )}

            {success && <p className={styles.successMessage}>{success}</p>}
            {error && <p className={styles.errorMessage}>{error}</p>}
          </form>
        )}

        {step === "forgot-email" && (
          <form onSubmit={handleRequestPasswordResetCode}>
            <div className={styles.stepHint}>
              {t("forgot_password_subtitle", {
                defaultValue:
                  "Enter your email and we will send you a reset code.",
              })}
            </div>

            <div className={styles.inputGroup}>
              <label>{t("email", { defaultValue: "Email" })}:</label>
              <input
                type="email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                placeholder="employee@company.com"
                required
                disabled={formDisabled}
              />
            </div>

            <button
              type="submit"
              className={styles.loginButton}
              disabled={formDisabled}
            >
              {loading
                ? t("sending", { defaultValue: "Sending..." })
                : t("send_reset_code", { defaultValue: "Send reset code" })}
            </button>

            <div className={styles.authFooter}>
              <button
                type="button"
                className={styles.textAction}
                onClick={goToLoginStep}
                disabled={formDisabled}
              >
                {t("back_to_login", { defaultValue: "Back to login" })}
              </button>
            </div>

            {success && <p className={styles.successMessage}>{success}</p>}
            {error && <p className={styles.errorMessage}>{error}</p>}
          </form>
        )}

        {step === "forgot-code" && (
          <form onSubmit={handleVerifyPasswordResetCode}>
            <div className={styles.stepHint}>
              {t("verify_reset_code_subtitle", {
                defaultValue: "Enter the code sent to your email address.",
              })}
            </div>

            <div className={styles.inputGroup}>
              <label>{t("email", { defaultValue: "Email" })}:</label>
              <input type="email" value={resetEmail} disabled />
            </div>

            <div className={styles.inputGroup}>
              <label>
                {t("verification_code", {
                  defaultValue: "Verification code",
                })}
                :
              </label>
              <input
                type="text"
                value={resetCode}
                onChange={(e) => setResetCode(e.target.value)}
                placeholder={t("verification_code", {
                  defaultValue: "Verification code",
                })}
                required
                disabled={formDisabled}
              />
            </div>

            <button
              type="submit"
              className={styles.loginButton}
              disabled={formDisabled}
            >
              {loading
                ? t("verifying", { defaultValue: "Verifying..." })
                : t("verify_code", { defaultValue: "Verify code" })}
            </button>

            <div className={styles.authActionsRow}>
              <button
                type="button"
                className={styles.textAction}
                onClick={handleResendPasswordResetCode}
                disabled={formDisabled}
              >
                {t("resend_code", { defaultValue: "Resend code" })}
              </button>

              <button
                type="button"
                className={styles.textAction}
                onClick={() => {
                  setStep("forgot-email");
                  setResetCode("");
                  setNewPassword("");
                  setError("");
                  setSuccess("");
                }}
                disabled={formDisabled}
              >
                {t("change_email", { defaultValue: "Change email" })}
              </button>
            </div>

            {success && <p className={styles.successMessage}>{success}</p>}
            {error && <p className={styles.errorMessage}>{error}</p>}
          </form>
        )}

        {step === "forgot-complete" && (
          <form onSubmit={handleCompletePasswordReset}>
            <div className={styles.stepHint}>
              {t("set_new_password_subtitle", {
                defaultValue: "Choose a new password for your account.",
              })}
            </div>

            <div className={styles.inputGroup}>
              <label>{t("email", { defaultValue: "Email" })}:</label>
              <input type="email" value={resetEmail} disabled />
            </div>

            <div className={styles.inputGroup}>
              <label>{t("password", { defaultValue: "New password" })}:</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder={t("password", { defaultValue: "New password" })}
                required
                disabled={formDisabled}
              />
            </div>

            <button
              type="submit"
              className={styles.loginButton}
              disabled={formDisabled}
            >
              {loading
                ? t("saving", { defaultValue: "Saving..." })
                : t("save_new_password", {
                    defaultValue: "Save new password",
                  })}
            </button>

            <div className={styles.authFooter}>
              <button
                type="button"
                className={styles.textAction}
                onClick={() => {
                  setStep("forgot-code");
                  setNewPassword("");
                  setError("");
                  setSuccess("");
                }}
                disabled={formDisabled}
              >
                {t("back", { defaultValue: "Back" })}
              </button>
            </div>

            {success && <p className={styles.successMessage}>{success}</p>}
            {error && <p className={styles.errorMessage}>{error}</p>}
          </form>
        )}
      </div>
    </div>
  );
}
